import { useState, useEffect, useRef } from 'react'
import sub from 'date-fns/sub'
import deepEqual from 'deep-equal'

import axios from 'utils/axios'
import { ORDER_HISTORY_START_DAYS, WIP_PAGE_DAYS } from 'utils/constants'

import urlSerialize from 'helpers/browser/urlSerialize'
import formatDateToUTC from 'helpers/datetime/formatDateToUTC'
import useConfiguration from 'hooks/useConfiguration'

const useOrdersInfinite = ({ params, manualStartDate, manualEndDate }) => {
  const [cachedParams, setCachedParams] = useState(params)
  const [infiniteLoading, setInfiniteLoading] = useState(false)
  const [data, setData] = useState(null)
  const [infiniteData, setInfiniteData] = useState([])
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const { instanceConfigurations } = useConfiguration()
  const startDays =
    parseInt(instanceConfigurations?.[ORDER_HISTORY_START_DAYS]) ||
    WIP_PAGE_DAYS
  const requestIdRef = useRef(0)
  const abortControllerRef = useRef(null)

  const handleGet = async (initial = false) => {
    // Cancel any in-flight request
    if (abortControllerRef.current) {
      await abortControllerRef.current.abort()
    }

    // Create new AbortController for this request
    abortControllerRef.current = new AbortController()

    // Increment request ID for this request
    const currentRequestId = ++requestIdRef.current

    const notInfinite = Boolean(manualStartDate)
    const sorted = Boolean(cachedParams['sort[0]'])

    const startDate =
      formatDateToUTC(manualStartDate) ||
      formatDateToUTC(
        sub(new Date(), {
          days: page === 1 ? startDays : startDays + (page - 1) * WIP_PAGE_DAYS,
        })
      )

    let endDate = !manualStartDate
      ? formatDateToUTC(
          sub(new Date(), {
            days:
              page === 1
                ? 0
                : startDays + (page - 1) * WIP_PAGE_DAYS - WIP_PAGE_DAYS,
          })
        )
      : formatDateToUTC(manualEndDate)

    if (initial && !notInfinite) {
      endDate = formatDateToUTC(new Date())
    }

    const urlParams = { ...cachedParams }

    if (!manualStartDate) {
      urlParams['date_created[start]'] = startDate
      urlParams['date_created[end]'] = endDate
    }

    const endpoint =
      Boolean(cachedParams) && `/orders?${urlSerialize(urlParams)}`

    if (endpoint) {
      if (page === 1 || notInfinite) {
        setLoading(true)
      } else {
        setInfiniteLoading(true)
      }
      try {
        const res = await axios.get(endpoint, {
          signal: abortControllerRef.current.signal,
        })

        // Only process the response if this is still the most recent request
        if (currentRequestId !== requestIdRef.current) {
          return
        }

        const orders = res.data._embedded?.orders || []

        if (!sorted && orders.length > 0) {
          orders.sort(
            (a, b) =>
              new Date(b.datetime_created) - new Date(a.datetime_created)
          )
        }

        if (notInfinite) {
          setData([res.data])
        } else {
          setInfiniteData(infiniteData.concat([res.data]))
          setData(null)
        }
      } catch (err) {
        // Only update state if this is still the most recent request and wasn't cancelled
        if (
          currentRequestId === requestIdRef.current &&
          err.name !== 'CanceledError'
        ) {
          setData(null)
        }
      } finally {
        // Only update loading state if this is still the most recent request
        if (currentRequestId === requestIdRef.current) {
          setLoading(false)
          setInfiniteLoading(false)
        }
      }
    }
  }

  useEffect(() => {
    if (page === 1) {
      handleGet(true)
    } else {
      handleGet()
    }
  }, [page, cachedParams])

  useEffect(() => {
    if (!deepEqual(cachedParams, params)) {
      setInfiniteData([])
      setData(null)
      setCachedParams(params)
      setPage(1)
    }
  }, [params])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [])

  const orders = data || infiniteData || []

  return {
    orders,
    allOrders: orders?.flatMap((data) => data?._embedded?.orders) ?? [],
    total: orders?.length
      ? orders.reduce((sum, orders) => sum + orders.total, 0)
      : 1,
    loading: loading || !orders?.length,
    additionalLoading: infiniteLoading,
    page,
    setPage: (page) => {
      setInfiniteLoading(true)
      setPage(page)
    },
    mutate: handleGet,
  }
}

export default useOrdersInfinite
